import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";

import { useRecoilValue, useSetRecoilState } from "recoil";
import { routeInfo, userCredentials } from "recoilState/GlobalState";

import { Buttons, HeaderButton } from "components";
import Filters from "components/Filters";
import { useFormik } from "formik";
import FilterAltOffRoundedIcon from "@mui/icons-material/FilterAltOffRounded";
import Add from "@mui/icons-material/Add";
import Cached from "@mui/icons-material/Cached";
import EditOutlined from "@mui/icons-material/EditOutlined";

import Modal from "components/UI/Modal/Modal";
import { ACTIONS } from "consts/Actions";
import { InsertModal, UpdateModal } from "./Actions";

import { useService, puedeEditarServicio } from "components/route/ServiceRoute";
import XGrid from "components/UI/Grid/XGrid";
import { usuariosInternos as titles } from "consts/titulos-de-tablas";
import styles from "styles/pages/Pages.module.scss";

import { connectionRolGet, merchantServicioUsuarioGet, rolGet } from "services/services-mia";
import { ESTADOS_CONEXION, GENEROS, esRolAdministrador, estaDeBaja } from "./consts";

/**
 * Usuarios internos del Programa Salud.
 *
 * Son los usuarios del SERVICIO (`servicioCod`), no los agentes: esta vista lista a
 * quienes tienen acceso al panel, con su rol y el estado de su vinculo.
 *
 * Arranca en solo lectura, con las columnas de Usuarios Globales. Las acciones y los
 * filtros se suman cuando esten definidos.
 */
/** Los filtros del buscador, sin nada aplicado. */
const FILTROS_VACIOS = { apellido: "", dni: "", rol: "", estado: "" };

/** Sin puntos ni mayusculas, para comparar lo tipeado contra lo que vino del servicio. */
const normalizar = (texto) => String(texto ?? "").toLowerCase().replace(/\./g, "");

/**
 * Aplica los filtros del buscador sobre las filas ya cargadas.
 *
 * En MEMORIA y no en el servicio porque `MerchantServicioUsuarioGet` no recibe apellido,
 * documento ni estado: solo el rol. Antes se mandaban todos al back, pero ese servicio ya no
 * esta en juego. Como ademas no tiene paginado y siempre trae todo, filtrar aca no se pierde
 * nada y ahorra una ida y vuelta por cada "Filtrar".
 *
 * Apellido y documento buscan por CONTENIDO —alcanza con una parte—; rol y estado son exactos
 * porque salen de un desplegable.
 */
const filtrarUsuarios = (filas, filtros) =>
    filas.filter((fila) => {
        const porApellido =
            !filtros.apellido ||
            normalizar(fila[titles[0].field]).includes(normalizar(filtros.apellido));
        const porDocumento =
            !filtros.dni ||
            normalizar(fila[titles[1].field]).includes(normalizar(filtros.dni));
        const porRol = !filtros.rol || fila.rolCod === filtros.rol;
        const porEstado = !filtros.estado || fila.estadoCod === filtros.estado;

        return porApellido && porDocumento && porRol && porEstado;
    });

export default function UsuariosInternos() {
  const credentials = useRecoilValue(userCredentials);
  const setRoute = useSetRecoilState(routeInfo);
  const { service } = useService();

  const [rowSelected, setRowSelected] = useState({ status: false, row: {} });
  const [modalDetails, setModalDetails] = useState({ isOpen: false });
  const [usuarios, setUsuarios] = useState([]);
  const [cargando, setCargando] = useState(false);

  // Los APLICADOS, separados de los que se estan tipeando: el buscador recien surte efecto
  // al tocar "Filtrar". No disparan una carga, solo recortan lo que ya esta en memoria.
  const [filtros, setFiltros] = useState(FILTROS_VACIOS);

  const formik = useFormik({
    initialValues: FILTROS_VACIOS,
    onSubmit: (values) => setFiltros(values),
  });
  // Habilita "Filtrar" y "Borrar filtros": sin nada cargado no hay nada que aplicar.
  const hayFiltros = Object.values(formik.values).some(Boolean);


  /**
   * Dos catalogos de roles, que NO son lo mismo.
   *
   *   rolesDelServicio  (`rolGet`)            todos los roles internos que existen. Llenan el
   *                                           filtro de la grilla, que es solo lectura.
   *   rolesAsignables   (`connectionRolGet`)  los que ESTE usuario puede otorgar. Llenan los
   *                                           selectores del alta y la modificacion.
   */
  const [rolesDelServicio, setRolesDelServicio] = useState([]);
  const [rolesAsignables, setRolesAsignables] = useState([]);

  /**
   * Si este usuario puede crear o modificar usuarios INTERNOS.
   *
   * `connectionRolGet` con `rolTipo: "I"` vacio significa que no hay ningun rol interno que
   * pueda otorgar, y sin rol no hay alta ni modificacion posible.
   *
   * Hace falta ademas de `puedeEditarServicio`, no en su lugar: un Operador tiene permiso de
   * edicion en el servicio —escribe sobre usuarios EXTERNOS— asi que esa guarda sola lo
   * dejaria entrar aca. El back lo rechaza igual; esto es para que no se entere al guardar.
   */
  const puedeGestionarInternos = rolesAsignables.length > 0;

  // El titulo va en un layout effect para que el Header no alcance a pintar el anterior.
  useLayoutEffect(() => {
    setRoute({ wordKey: "Usuarios internos - Programa Salud RN" });
  }, [setRoute]);

  useEffect(() => {
    (async () => {
      // Los dos con 'I': esta vista solo administra usuarios internos.
      const [delServicio, asignables] = await Promise.all([
        rolGet(service?.servicioCod, credentials, "I"),
        connectionRolGet(service?.servicioCod, credentials, "I"),
      ]);
      setRolesDelServicio(delServicio?.result ?? []);
      setRolesAsignables(asignables?.result ?? []);
    })();
    getUsuarios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service?.servicioCod]);

  const borrarFiltros = () => {
    formik.setValues(FILTROS_VACIOS);
    setFiltros(FILTROS_VACIOS);
  };

  const usuariosVisibles = useMemo(
    () => filtrarUsuarios(usuarios, filtros),
    [usuarios, filtros]
  );

  /**
   * Los roles PRESENTES en la tabla, como respaldo del filtro.
   *
   * El filtro se llena con el catalogo (`rolGet`), pero ese servicio llego a volver vacio
   * —siendo Visualizador no devolvia nada— y con la lista vacia el desplegable quedaba
   * deshabilitado justo para quien solo puede mirar, que es el que mas lo necesita.
   *
   * Estos salen de las filas que ya estan cargadas, asi que no dependen de ningun servicio.
   * Son menos: solo los roles que realmente hay en la lista, no todos los que existen.
   */
  const rolesEnLaTabla = useMemo(() => {
    const porCod = new Map();
    usuarios.forEach((fila) => {
      if (fila.rolCod && !porCod.has(fila.rolCod)) {
        porCod.set(fila.rolCod, {
          rolCod: fila.rolCod,
          rolNombre: fila[titles[5].field] ?? fila.rolCod,
        });
      }
    });
    return [...porCod.values()].sort((a, b) =>
      String(a.rolNombre).localeCompare(String(b.rolNombre))
    );
  }, [usuarios]);

  // El catalogo manda; los de la tabla son el respaldo (ver `rolesEnLaTabla`).
  const opcionesRol = rolesDelServicio.length ? rolesDelServicio : rolesEnLaTabla;

  const abrirModal = ({ procede, title }) =>
    setModalDetails({ procede, title, isOpen: true });
  const cerrarModal = () => setModalDetails({ isOpen: false });

  /**
   * Trae los usuarios INTERNOS del servicio.
   *
   * `rolTipo: "I"` es lo que los separa de los externos, y lo decide el back. Reemplaza al
   * recorte por nombre de rol que haciamos antes, que era un parche nuestro: comparaba
   * "operador", "visualizador" y "administrador" contra el `rolNombre` y se habria roto en
   * silencio el dia que el back renombrara alguno.
   *
   * No tiene paginado ni params de busqueda, asi que siempre trae todo y tanto el filtrado
   * como la paginacion los hace el front. Para el volumen de usuarios internos de un
   * organismo es lo razonable, y ademas evita una ida y vuelta por cada filtro.
   */
  const getUsuarios = async () => {
    setRowSelected({ status: false, row: {} });
    setUsuarios([]);
    setCargando(true);

    const { result } = await merchantServicioUsuarioGet(
      {
        servicioCod: service?.servicioCod,
        rolTipo: "I",
      },
      credentials
    );

    // Sin filtrar por rol: los externos ya no vienen en la respuesta, los deja afuera el
    // `rolTipo: "I"` del servicio.
    const filas = (result ?? []).map((usuario) => ({
      id: usuario.merchantServicioUsuarioId,
      // No son columnas: las van a necesitar las acciones cuando existan.
      usuarioId: usuario.usuarioId,
      rolCod: usuario.rolCod,
      estadoCod: usuario.relacionStatusCod,

      // Armado con los dos campos y no con `usuarioNombreCompleto`, que llega como
      // "Juarez Manuel": sin separador no se sabe donde termina el apellido y empieza el
      // nombre, y con apellidos o nombres compuestos es directamente ambiguo. El
      // `filter(Boolean)` evita la coma colgando si falta alguno de los dos.
      //
      // Es el mismo criterio que usa la lista de agentes.
      [titles[0].field]: [usuario.usuarioApellidos, usuario.usuarioNombres]
        .filter(Boolean)
        .join(", "),
      [titles[1].field]: usuario.usuarioNroDeDocumentoX,
      // El codigo que guarda MIA ("M") no se muestra: va la misma etiqueta que ofrece el
      // selector del alta. Si llegara un codigo que no esta en la lista, se muestra crudo.
      [titles[2].field]:
        GENEROS.find((g) => g.cod === usuario.usuarioSexo)?.label ??
        usuario.usuarioSexo,
      [titles[3].field]: usuario.usuarioFechaDeNacimiento,
      // El mail es columna Y dato del modal de modificacion: una sola key sirve para las dos
      // cosas, no hace falta repetirlo entre los campos que no se muestran.
      [titles[4].field]: usuario.usuarioMail,
      [titles[5].field]: usuario.rolNombre,
      // "Onboard" es si la persona completo su registro en la app.
      [titles[6].field]:
        usuario.usuarioId === null
          ? "Pendiente"
          : usuario.usuarioStatusRegistro === "OK"
            ? "Completo"
            : usuario.usuarioStatusRegistro === "E1"
              ? "Observado"
              : "Incompleto",
      [titles[7].field]: usuario.usuarioFechaDeAlta,
      [titles[8].field]: usuario.relacionStatus,
      [titles[9].field]: usuario.usuarioFecha,
      }));

    setUsuarios(filas);
    setCargando(false);
  };

  return (
    <main className={styles.main}>
      <div className={styles["filters-actions"]}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <Filters
            formik={formik}
            disabled={!hayFiltros}
            inputsRender={[
              { type: "text", name: "apellido", label: "Apellido", disabled: false },
              { type: "text", name: "dni", label: "Documento", disabled: false },
              {
                type: "select",
                name: "rol",
                label: "Rol",
                disabled: !opcionesRol.length,
                options: opcionesRol,
                firstOptionKey: "rolCod",
                firstOptionValue: "rolCod",
                firstOptionName: "rolNombre",
              },
              {
                type: "select",
                name: "estado",
                label: "Estado",
                disabled: false,
                options: ESTADOS_CONEXION,
                firstOptionKey: "cod",
                firstOptionValue: "cod",
                firstOptionName: "label",
              },
            ]}
          />
          <HeaderButton
            text={"Borrar filtros"}
            onClick={borrarFiltros}
            disabled={!hayFiltros}
          >
            <FilterAltOffRoundedIcon />
          </HeaderButton>
        </div>
        <Buttons>
          <HeaderButton
            text={"Actualizar"}
            onClick={() => getUsuarios()}
            disabled={Boolean(rowSelected.row.id)}
          >
            <Cached />
          </HeaderButton>
          <HeaderButton
            text={"Nuevo Usuario Interno"}
            handleClick={abrirModal}
            data={{ title: "Nuevo Usuario Interno", procede: ACTIONS.INSERT }}
            disabled={!puedeEditarServicio(service) || !puedeGestionarInternos || Boolean(rowSelected.row.id)}
          >
            <Add />
          </HeaderButton>
          <HeaderButton
            text={"Modificar Usuario Interno"}
            handleClick={abrirModal}
            data={{ title: "Modificar Usuario Interno", procede: ACTIONS.UPDATE }}
            /*
              Cuatro motivos para no poder modificar, y cualquiera alcanza:

                - sin permiso de escritura en el servicio (Visualizador),
                - sin roles internos asignables (Operador),
                - sin fila seleccionada,
                - la fila es de un administrador —un admin no se modifica a si mismo ni a
                  otro—, o esta dada de baja, que es un estado terminal para todos.

              Crear sigue habilitado: los tres roles se pueden dar de alta.
            */
            disabled={
              !puedeEditarServicio(service) ||
              !puedeGestionarInternos ||
              !rowSelected.row.id ||
              esRolAdministrador(rowSelected.row.rolCod) ||
              estaDeBaja(rowSelected.row.estadoCod)
            }
          >
            <EditOutlined />
          </HeaderButton>
        </Buttons>
      </div>

      <div className={styles.container}>
        <XGrid
          rows={usuariosVisibles}
          titles={titles}
          loading={cargando}
          secondarySelected={rowSelected}
          setSecondarySelected={setRowSelected}
        />
      </div>

      <Modal
        size="sm"
        open={modalDetails.isOpen}
        title={modalDetails.title}
        onClose={cerrarModal}
      >
        {modalDetails.procede === ACTIONS.INSERT && (
          <InsertModal
            roles={rolesAsignables}
            servicio={service}
            updateList={getUsuarios}
            closeModal={cerrarModal}
          />
        )}
        {modalDetails.procede === ACTIONS.UPDATE && (
          <UpdateModal
            roles={rolesAsignables}
            row={rowSelected.row}
            updateList={getUsuarios}
            closeModal={cerrarModal}
          />
        )}
      </Modal>
    </main>
  );
}
